import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { ApplicationsStore } from 'src/app/shared/stores/applications/applications.store';
import { ApplicationsService } from 'src/app/shared/services/applications/applications.service';
import { ClientStore } from 'src/app/shared/stores/client/client.store';
import { DocumentStore } from 'src/app/shared/stores/document/document.store';
import { DocumentService } from 'src/app/shared/services/document/document.service';

/**
 * Resolver to ensure applications and documents are loaded before the documents page renders.
 * This prevents race conditions where users might try to upload files before applications are loaded,
 * which would result in invalid CaseId (0) being sent to the API.
 */
export const documentsResolver: ResolveFn<boolean> = () => {
  const applicationsStore = inject(ApplicationsStore);
  const applicationsService = inject(ApplicationsService);
  const documentStore = inject(DocumentStore);
  const documentService = inject(DocumentService);
  const clientStore = inject(ClientStore);

  const client = clientStore.client();
  const currentApp = applicationsStore.currentApplication();

  // Validation: Ensure client exists
  if (!client?.clientId || !client?.brokerageId) {
    console.error('[DocumentsResolver] No client available, cannot load documents');
    return of(false);
  }

  // Check if applications are already loaded
  const hasApplications = !!currentApp?.ApplicationId;

  // Check if documents are already loaded for this specific client/application context
  const hasDocumentsForContext = hasApplications &&
    documentStore.hasDocumentsForContext()(client.clientId, currentApp.ApplicationId);

  console.log('[DocumentsResolver] Current state:', {
    hasApplications,
    hasDocumentsForContext,
    applicationId: currentApp?.ApplicationId,
    clientId: client.clientId
  });

  // If both applications and documents are loaded for this context, skip loading
  if (hasApplications && hasDocumentsForContext) {
    console.log('[DocumentsResolver] Data already loaded for this context, skipping API calls');
    return of(true);
  }

  // If applications not loaded, load them first
  if (!hasApplications) {
    console.log('[DocumentsResolver] Loading applications...');
    return applicationsService.get(client.clientId).pipe(
      switchMap(() => {
        const app = applicationsStore.currentApplication();
        if (!app?.ApplicationId) {
          console.error('[DocumentsResolver] No applications found for client');
          return of(false);
        }

        // Applications loaded, now load documents
        console.log('[DocumentsResolver] Applications loaded, loading documents...');
        return from(
          documentStore.loadDocuments(
            documentService,
            client.clientId,
            app.ApplicationId,
            client.brokerageId
          )
        ).pipe(
          map(() => true),
          catchError((error) => {
            console.error('[DocumentsResolver] Failed to load documents:', error);
            return of(false);
          })
        );
      }),
      catchError((error) => {
        console.error('[DocumentsResolver] Failed to load applications:', error);
        return of(false);
      })
    );
  }

  // Applications already loaded, just load documents
  return from(
    documentStore.loadDocuments(
      documentService,
      client.clientId,
      currentApp.ApplicationId,
      client.brokerageId
    )
  ).pipe(
    map(() => true),
    catchError((error) => {
      console.error('[DocumentsResolver] Failed to load documents:', error);
      return of(false);
    })
  );
};
